import React from 'react';
import { useTranslation } from 'react-i18next';
import { unescapeHTML, titleize } from 'underscore.string';
import { NavLink } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import CargomaticLogo from 'img/cargomatic-logo-dark.png';
import SessionStore from 'stores/SessionStore';
import AccountAccessRequestModal from 'components/modules/user/AccountAccessRequestModal';
import classNames from 'classnames';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import Navbar from '../v2/navs/Navbar';
import UserActionCreators from '../../actions/UserActionCreators';
import DropdownMenu from '../v2/dropdowns/DropdownMenu';
import DollarIcon from '../v2/icons/DollarIcon';
import NavbarOverlayBanner from '../v2/navbar-overlay-banner';
import ShipmentSearch from './shipment-search';
import { Avatar } from '../modules/user/Avatar';
import LanguageSelector from './language-selector';

function logout() {
  UserActionCreators.logout();
}

export default function CarrierNavigation() {
  const carrier = SessionStore.getUser();
  const hasAppointmentAccess =
    SessionStore.carrierHasAppointmentsAccess(carrier);

  const { t } = useTranslation();

  function renderMobileNav(onClose) {
    return (
      <>
        <NavLink
          className="nav-item"
          to="/shipments"
          onClick={onClose}
          key="mobile-shipments"
        >
          <span>{t('MY_SHIPMENTS')}</span>
        </NavLink>
        <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
          <NavLink
            className="nav-item"
            to="/carrier-payouts"
            onClick={onClose}
            key="mobile-carrier-payouts"
          >
            <span>{t('MY_PAYMENTS')}</span>
          </NavLink>
        </AuthorizationWrapper>
        <NavLink
          className="nav-item"
          to="/loadboard"
          onClick={onClose}
          key="mobile-loadboard"
        >
          <span>{titleize(t('OFFER', { count: 2 }))}</span>
        </NavLink>
        <NavLink
          className="nav-item mobile-carrier-menu offers-offerlist heap"
          to="/drivers"
          onClick={onClose}
          key="mobile-drivers"
        >
          <span>{t('MY_DRIVERS')}</span>
        </NavLink>
        {hasAppointmentAccess && (
          <NavLink className="nav-item" to="/appointments" key="appointments">
            <span>{t('MY_APPOINTMENTS')}</span>
          </NavLink>
        )}
        <NavLink
          className="nav-item"
          to="/carrier-center"
          onClick={onClose}
          key="mobile-profiles"
        >
          <span>{titleize(t('PROFILE', { count: 1 }))}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to={`/carriers/dashboard?carrier=${carrier._id}`}
          onClick={onClose}
          key="mobile-dashboard"
        >
          <span>{t('PENDING_TASKS')}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to="/documents"
          onClick={onClose}
          key="mobile-documents"
        >
          <span>{t('MY_DOCUMENTS')}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to="/billing"
          onClick={onClose}
          key="mobile-billing"
        >
          <span>{t('BILLING')}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to="/notifications"
          onClick={onClose}
          key="mobile-notifications"
        >
          <span>{titleize(t('NOTIFICATION', { count: 2 }))}</span>
        </NavLink>
        ,
        <div
          className={classNames('nav-item', {
            hidden: LANGUAGE_SELECTION_DISABLED,
          })}
        >
          <LanguageSelector mobile />
        </div>
        ,
        <a href="#" className="nav-item" onClick={logout} key="mobile-signout">
          {t('SIGN_OUT')}
        </a>
      </>
    );
  }
  function renderRightNav() {
    const name = SessionStore.getUser().name;
    const userId = SessionStore.getUser()._id;
    let profileButton = null;

    profileButton = (
      <div
        title={unescapeHTML(name)}
        className="flex text-sm bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-gray-800 focus:ring-white"
        aria-haspopup="true"
      >
        <Avatar
          key={userId}
          userId={userId}
          altName="Logo"
          className="w-8 h-8 rounded-full"
        />
      </div>
    );

    return [
      <div
        className={classNames(
          'flex items-center justify-center nav-item mr-2',
          { hidden: LANGUAGE_SELECTION_DISABLED },
        )}
      >
        <LanguageSelector />
      </div>,
      <DropdownMenu
        title="Carrier Center"
        button={profileButton}
        isDark
        key="main-dropdown"
      >
        <Menu.Item key="profile">
          <NavLink
            activeClassName="active-light"
            className="nav-item"
            to="/carrier-center"
          >
            <span>{titleize(t('PROFILE', { count: 1 }))}</span>
          </NavLink>
        </Menu.Item>
        <Menu.Item key="documents">
          <NavLink
            activeClassName="active-light"
            className="nav-item"
            to="/documents"
          >
            <span>{t('MY_DOCUMENTS')}</span>
          </NavLink>
        </Menu.Item>
        <Menu.Item key="billing">
          <NavLink
            activeClassName="active-light"
            className="nav-item"
            to="/billing"
          >
            <span>{t('BILLING')}</span>
          </NavLink>
        </Menu.Item>
        <Menu.Item key="notifications">
          <NavLink
            activeClassName="active-light"
            className="nav-item"
            to="/notifications"
          >
            <span>{titleize(t('NOTIFICATION', { count: 2 }))}</span>
          </NavLink>
        </Menu.Item>
        <Menu.Item key="sign-out">
          <a href="#" className="nav-item" onClick={logout}>
            {t('SIGN_OUT')}
          </a>
        </Menu.Item>
      </DropdownMenu>,
    ];
  }
  const brandLogo = (
    <NavLink to={`/carriers/dashboard?carrier=${carrier._id}`}>
      <img alt="Cargomatic" src={CargomaticLogo} style={{ height: 25 }} />
    </NavLink>
  );

  const overlay = (
    <NavbarOverlayBanner text={SessionStore.getUser().name || ''} showButton />
  );

  const isAuthorized = carrier.authorized;
  const isLoggedInAs = !!SessionStore.getLoginAs();

  return (
    <>
      <Navbar
        logo={brandLogo}
        renderRightNav={renderRightNav}
        renderMobileNav={renderMobileNav}
      >
        {isAuthorized && (
          <>
            <NavLink
              key="loadboard"
              to="/loadboard"
              className="flex-grow-0 flex-shrink-0 nav-item cursor button button-green mobile-carrier-nav-bar offers-offerlist heap"
            >
              <div className="flex items-center space-x-2">
                <DollarIcon />
                <span className="">{titleize(t('OFFER', { count: 2 }))}</span>
              </div>
            </NavLink>
            <NavLink
              className="nav-item"
              to={`/carriers/dashboard?carrier=${carrier._id}`}
              key="dashboard"
            >
              <span>{t('CARRIER_DASHBOARD_PENDING_TASK')}</span>
            </NavLink>
            <NavLink className="nav-item" to="/shipments" key="shipments">
              <span>{t('MY_SHIPMENTS')}</span>
            </NavLink>
            <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
              <NavLink
                className="nav-item"
                to="/carrier-payouts"
                key="carrier-payouts"
              >
                <span>{t('MY_PAYMENTS')}</span>
              </NavLink>
            </AuthorizationWrapper>
            {hasAppointmentAccess && (
              <NavLink
                className="nav-item"
                to="/appointments"
                key="appointments"
              >
                <span>{t('MY_APPOINTMENTS')}</span>
              </NavLink>
            )}
            <NavLink className="nav-item" to="/drivers" key="drivers">
              <span>{t('MY_DRIVERS')}</span>
            </NavLink>
            <NavLink className="nav-item" to="/driver-tracking" key="live-view">
              <span>{t('LIVE_VIEW')}</span>
            </NavLink>
            <div className="w-60">
              <ShipmentSearch />
            </div>
          </>
        )}
      </Navbar>
      <div>{isLoggedInAs && overlay}</div>
      <AccountAccessRequestModal />
    </>
  );
}
